/**
 * The boot splash — the one loading screen a hard reload shows, from the first paint until the
 * route the visitor asked for is genuinely ready to look at.
 *
 * Why it is markup in `index.html` and only *driven* from here: everything this module could
 * render would arrive with the bundle, and the bundle is the slowest thing on the critical
 * path. The screen a visitor sees while ~1 MB of JS downloads has to exist before any of it
 * runs, so `index.html` owns the markup and the CSS, and this file owns the four hooks
 * (`data-boot="splash" | "fill" | "label" | "percent"`) and the lifecycle. A markup rename
 * breaks `boot-splash.test.ts`, which mounts the real `index.html`, not a fixture.
 *
 * The stage table lives here rather than in `main.ts` because it is presentation: weights are a
 * rough share of the wait, not a promise, and the labels are resolved through `t()` at the
 * moment they are set — never at module scope, which would freeze the language the tab booted
 * in. `main.ts` drives it with `BootProgress` (`arena/boot-loading.ts`), the same monotonic
 * machine the arena's loading screen and the shell's hand-off use: one bar behaviour in the
 * app, and it only ever moves right.
 */
import type { BootStage } from './arena/boot-loading';
import { t } from './i18n';

/** Matches the `transition: opacity 320ms` in `index.html`. Under reduced motion the transition
 * is `none` there, so the node simply sits invisible (and non-interactive) for this long. */
const FADE_MS = 320;

/**
 * The boot's stages, in order, weighted by how long each actually keeps a visitor waiting on a
 * cold load.
 *
 * `hero` is over half the bar because it is over half the wait: the landing's hero is the arena
 * renderer (Three, the map's PBR textures, an HDRI and five rigged GLBs), and it is the whole
 * reason this screen exists — the page used to reveal with a hole where the hero goes. Routes
 * other than `/` never enter that stage; they finish at `mount` and the bar jumps to full,
 * which is honest: for them there is nothing left to wait for.
 */
export const BOOT_SPLASH_STAGES: readonly BootStage[] = [
  { id: 'settings', label: 'Loading', labelKey: 'boot.settings', weight: 12 },
  { id: 'session', label: 'Signing in', labelKey: 'boot.session', weight: 12 },
  { id: 'mount', label: 'Building the page', labelKey: 'boot.page', weight: 18 },
  { id: 'hero', label: 'Preparing the arena', labelKey: 'boot.hero', weight: 58 },
];

/**
 * A stage's label in the visitor's language, falling back to its English string.
 *
 * `t()` renders a missing key as the key itself, and a loading screen is the last surface that
 * can afford to put `boot.hero` in front of a person — so every lookup falls back rather than
 * trusting the catalogue to be complete. Same rule, same reason, as `stageLabel` in
 * `arena/boot-loading.ts`.
 */
export function bootStageLabel(stage: BootStage | null | undefined): string {
  const translate = (key: string, fallback: string): string => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  if (!stage) return translate('play.loading.ready', 'Ready');
  return stage.labelKey ? translate(stage.labelKey, stage.label) : stage.label;
}

export interface BootSplash {
  /** 0..1. Monotonic — a bar that goes backwards reads as a failure, and progress here is
   * assembled from several parallel fetches whose reports do arrive out of order. */
  setProgress(fraction: number): void;
  setLabel(text: string): void;
  /** Fades the splash out and removes it. Idempotent, and it uncovers the page on the same
   * frame it is called: a splash that outlives its removal path is a dead screen over a
   * working app. */
  finish(): void;
}

/**
 * Claims the splash node and returns a handle to it, or **null when there is no node**.
 *
 * Null is a normal answer, not a failure: `/arena.html` is a separate entry with its own
 * loading screen (`mountBootScreen`), and a jsdom mount test has no `index.html` behind it.
 * Every caller treats the handle as optional (`splash?.setProgress(…)`).
 *
 * "Take", because the node is claimed on the first call — a second call finds it already
 * claimed and answers null rather than handing out a second handle that would fight the first
 * over the same bar.
 */
export function takeBootSplash(root: ParentNode = document): BootSplash | null {
  const node = root.querySelector<HTMLElement>('[data-boot="splash"]:not([data-state])');
  if (!node) return null;
  node.dataset.state = 'live';

  const fill = node.querySelector<HTMLElement>('[data-boot="fill"]');
  const label = node.querySelector<HTMLElement>('[data-boot="label"]');
  const percent = node.querySelector<HTMLElement>('[data-boot="percent"]');

  let shown = 0;
  let finished = false;

  const setProgress = (fraction: number): void => {
    if (finished) return;
    const next = Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0));
    if (next <= shown) return;
    shown = next;
    const pct = Math.round(next * 100);
    if (fill) fill.style.width = `${pct}%`;
    if (percent) percent.textContent = `${pct}%`;
  };

  const setLabel = (text: string): void => {
    if (finished || !label || !text) return;
    label.textContent = text;
  };

  const finish = (): void => {
    if (finished) return;
    finished = true;
    // Full bar first: the last thing a visitor sees the splash do is complete, not stop at 94%.
    shown = 1;
    if (fill) fill.style.width = '100%';
    if (percent) percent.textContent = '100%';
    if (label) label.textContent = bootStageLabel(null);
    node.dataset.state = 'done';
    node.setAttribute('aria-busy', 'false');
    // Removed after the fade — but `[data-state="done"]` has already made it transparent and
    // click-through, so a swallowed timer (a backgrounded tab throttling timeouts, say) costs
    // an invisible node in the DOM and never a blocked page.
    window.setTimeout(() => node.remove(), FADE_MS);
  };

  return { setProgress, setLabel, finish };
}
